import React, { useCallback, useEffect, useRef, useState } from "react";
import { Maximize2 as ExpandIcon } from "lucide-react";
import { Md360 } from "react-icons/md";

interface Exterior360ViewerLabels {
  noImages?: string;
  goToImage?: string;
  next?: string;
  previous?: string;
  dragHint?: string;
  fullscreen?: string;
  exitFullscreen?: string;
}

interface Exterior360ViewerProps {
  imageUrls: string[];
  className?: string;
  labels?: Exterior360ViewerLabels;
  /** Compact preview mode (smaller). Omit for normal/fullscreen. */
  compact?: boolean;
  /** Fill available space (no max-width when compact) */
  fill?: boolean;
  /** Show fullscreen button. Requires onFullscreenClick. */
  showFullscreenButton?: boolean;
  onFullscreenClick?: () => void;
}

/** Pixels to drag before advancing one frame – low = super sensitive (Dubizzle-style) */
const DRAG_THRESHOLD = 10;

/**
 * 360 frame viewer – drag/swipe with mouse or finger, or use slider.
 * Mimics 3D model rotation: drag left = rotate right, drag right = rotate left.
 */
export function Exterior360Viewer({
  imageUrls,
  className,
  labels = {},
  compact = false,
  fill = false,
  showFullscreenButton = false,
  onFullscreenClick,
}: Exterior360ViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragAccumRef = useRef(0);
  const lastXRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const count = imageUrls.length;
  const hasImages = count > 0;

  /** Looping: at end → wrap to start; at start going back → wrap to end */
  const goTo = useCallback(
    (dir: -1 | 1) => {
      if (!count) return;
      setCurrentIndex((i) => {
        let next = i + dir;
        if (next >= count) next = 0;
        else if (next < 0) next = count - 1;
        return next;
      });
    },
    [count],
  );

  const setIndex = useCallback(
    (i: number) => {
      if (!count) return;
      let idx = Math.round(i);
      if (idx >= count) idx = idx % count;
      else if (idx < 0) idx = ((idx % count) + count) % count;
      setCurrentIndex(Math.max(0, Math.min(count - 1, idx)));
    },
    [count],
  );

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (e.deltaX !== 0) {
        goTo(e.deltaX > 0 ? 1 : -1);
        return;
      }
      if (e.deltaY > 0) goTo(1);
      else if (e.deltaY < 0) goTo(-1);
    },
    [goTo],
  );

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    lastXRef.current = e.clientX;
    dragAccumRef.current = 0;
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !count) return;
      const dx = e.clientX - lastXRef.current;
      lastXRef.current = e.clientX;
      dragAccumRef.current += dx;
      const threshold = DRAG_THRESHOLD;
      while (dragAccumRef.current >= threshold) {
        goTo(1);
        dragAccumRef.current -= threshold;
      }
      while (dragAccumRef.current <= -threshold) {
        goTo(-1);
        dragAccumRef.current += threshold;
      }
    },
    [isDragging, count, goTo],
  );

  const handleMouseUp = useCallback(() => setIsDragging(false), []);
  const handleMouseLeave = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) =>
      handleMouseMove(e as unknown as React.MouseEvent);
    const onUp = () => handleMouseUp();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const touchAccumRef = useRef(0);
  const touchStartXRef = useRef(0);
  const isTouchingRef = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX);
    touchStartXRef.current = e.touches[0].clientX;
    touchAccumRef.current = 0;
    isTouchingRef.current = true;
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isTouchingRef.current || !count) return;
      const x = e.touches[0].clientX;
      const dx = x - touchStartXRef.current;
      touchStartXRef.current = x;
      touchAccumRef.current += dx;
      const threshold = DRAG_THRESHOLD;
      while (touchAccumRef.current >= threshold) {
        goTo(1);
        touchAccumRef.current -= threshold;
      }
      while (touchAccumRef.current <= -threshold) {
        goTo(-1);
        touchAccumRef.current += threshold;
      }
    },
    [count, goTo],
  );

  const handleTouchEnd = useCallback(() => {
    setTouchStart(null);
    isTouchingRef.current = false;
  }, []);

  // Prefetch neighbors (with looping indices)
  useEffect(() => {
    const preload = (i: number) => {
      if (!count) return;
      const idx = ((i % count) + count) % count;
      const img = new Image();
      img.src = imageUrls[idx];
    };
    preload(currentIndex - 1);
    preload(currentIndex + 1);
  }, [currentIndex, count, imageUrls]);

  if (!hasImages) {
    return (
      <div
        className={`flex items-center justify-center text-muted-foreground text-sm ${className ?? ""}`}
      >
        {labels.noImages ?? "Inga bilder att visa"}
      </div>
    );
  }

  const url = imageUrls[currentIndex];

  return (
    <div
      className={`space-y-3 ${compact && !fill ? "max-w-[280px]" : ""} ${className ?? ""}`}
    >
      <div
        ref={containerRef}
        className={`relative w-full overflow-hidden rounded-lg bg-muted select-none cursor-grab active:cursor-grabbing touch-pan-y min-h-[180px] ${isDragging ? "cursor-grabbing" : ""}`}
        style={{ aspectRatio: "16/9" }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={url}
          alt={`Frame ${currentIndex + 1}`}
          className="pointer-events-none w-full h-full object-contain"
          draggable={false}
        />
        <div className="absolute top-2 left-2 right-2 flex justify-between items-center gap-2">
          {!compact && (
            <span className="text-xs text-white/90 bg-black/50 px-2 py-1 rounded truncate max-w-[140px]">
              {labels.dragHint ?? "Dra för att rotera · scrolla · swipe"}
            </span>
          )}
          {showFullscreenButton && onFullscreenClick && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onFullscreenClick();
              }}
              className="shrink-0 p-1.5 rounded bg-black/50 hover:bg-black/70 text-white transition-colors"
              title={labels.fullscreen ?? "Helskärm"}
              aria-label={labels.fullscreen ?? "Helskärm"}
            >
              <ExpandIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      {count > 1 && (
        <div className="flex items-center gap-3">
          <Md360
            className="h-5 w-5 shrink-0 text-muted-foreground"
            aria-hidden
          />
          <input
            type="range"
            min={0}
            max={count - 1}
            step={1}
            value={currentIndex}
            onChange={(e) => setIndex(Number(e.target.value))}
            title={labels.dragHint ?? "Rotera 360°"}
            aria-label={labels.goToImage ?? "Rotera 360-vy"}
            className="flex-1 h-3 rounded-full appearance-none bg-muted cursor-grab [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:h-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:ring-2 [&::-webkit-slider-thumb]:ring-background [&::-webkit-slider-thumb]:-mt-1 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:ring-2 [&::-moz-range-thumb]:ring-background"
          />
        </div>
      )}
    </div>
  );
}
